import { Component, Inject, TemplateRef } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';

export type DeleteDialogDataWithTemplate = {
  header: string;
  formGroup?: FormGroup;
  deleteText: string;
  secondaryText?: string;
  noText?: string;
  template?: TemplateRef<any>;
  bottomSection?: TemplateRef<any>;
  onDelete: () => Promise<boolean>; //a function that is called when clicking okay. If returns true, closes modal
  onSecondary?: () => void;
};
export type DeleteDialogDataWithTemplateAndFormProps = DeleteDialogDataWithTemplate & {
  width: number;
  disableClose: boolean;
};
export const deleteDefaults: DeleteDialogDataWithTemplateAndFormProps = {
  header: 'Delete?',
  deleteText: 'Delete',
  noText: 'Cancel',
  onDelete: async () => true,
  width: 600,
  disableClose: true
};

@Component({
  selector: 'dialog-delete',
  templateUrl: 'delete-dialog.component.html',
  styleUrls: ['./delete-dialog.component.scss'],
  standalone: false
})
export class DeleteDialogComponent {
  public deleteDialogAction = DeleteDialogAction;
  public textMatcherValue = '';

  constructor(
    public dialogRef: MatDialogRef<DeleteDialogComponent, DeleteDialogAction>,
    @Inject(MAT_DIALOG_DATA)
    public data: DeleteDialogDataWithTemplate
  ) {}

  private onDeleteIsSuccessful = async (): Promise<boolean> => {
    if (!this.data.onDelete) {
      return true;
    }

    return await this.data.onDelete();
  };
  clickClose = async (action: DeleteDialogAction): Promise<DeleteDialogAction> => {
    switch (action) {
      case DeleteDialogAction.Delete:
        if (this.data.formGroup?.invalid) {
          this.data.formGroup.markAllAsTouched();
          return null;
        }
        try {
          const isSuccess = await this.onDeleteIsSuccessful();

          if (isSuccess) {
            this.dialogRef.close(DeleteDialogAction.Delete);
            return DeleteDialogAction.Delete;
          }
        } catch (e) {
          this.dialogRef.close(null); //assume any error is handled by snack error or HTTP redirection, but we need to close the modal
        }
        return null; //onDelete promise failed or returned false
      case DeleteDialogAction.Secondary:
        if (this.data.onSecondary) {
          await this.data.onSecondary();
        }
        this.dialogRef.close(DeleteDialogAction.Secondary);
        return DeleteDialogAction.Secondary;
      default:
        this.dialogRef.close(action);
        return action;
    }
  };

  isDeleteButtonDisabled() {
    return this.textMatcherValue.toUpperCase() !== this.getTextToDisplay();
  }

  static open = (
    dialog: MatDialog,
    configOverrides: Partial<DeleteDialogDataWithTemplate> = {}
  ): Promise<DeleteDialogAction> =>
    DeleteDialogComponent.openFromComponent(
      dialog,
      DeleteDialogComponent.buildConfigFromBaseAndOverRides(deleteDefaults, configOverrides)
    );

  static openFromDirective = (
    dialog: MatDialog,
    defaults: DeleteDialogDataWithTemplateAndFormProps,
    configOverrides: Partial<DeleteDialogDataWithTemplate> = {}
  ): Promise<DeleteDialogAction> =>
    DeleteDialogComponent.openFromComponent(
      dialog,
      DeleteDialogComponent.buildConfigFromBaseAndOverRides(defaults, configOverrides)
    );

  private static openFromComponent = (
    dialog: MatDialog,
    options: MatDialogConfig<DeleteDialogDataWithTemplate>
  ): Promise<DeleteDialogAction> =>
    lastValueFrom(dialog.open(DeleteDialogComponent, options).afterClosed()).then(
      (closeType: DeleteDialogAction) => {
        return closeType;
      }
    );

  private static buildConfigFromBaseAndOverRides = (
    base: DeleteDialogDataWithTemplateAndFormProps,
    configOverrides: Partial<DeleteDialogDataWithTemplate>
  ): MatDialogConfig<DeleteDialogDataWithTemplate> => ({
    width: `${base.width ?? 683}px`,
    maxWidth: '95vw',
    disableClose: base.disableClose ?? true,
    autoFocus: 'modal',
    data: {
      header: base.header,
      deleteText: base.deleteText,
      secondaryText: base.secondaryText,
      noText: base.noText,
      template: base.template,
      bottomSection: base.bottomSection,
      formGroup: base.formGroup,
      onDelete: base.onDelete,
      onSecondary: base.onSecondary,
      ...configOverrides
    }
  });

  getTextToDisplay(): string {
    if (this.data.deleteText.toUpperCase().includes('UNASSIGN')) {
      return 'UNASSIGN';
    }
    return 'DELETE';
  }
}

export enum DeleteDialogAction {
  Delete,
  Secondary,
  Cancel
}
